import React, { useLayoutEffect, useRef, useState } from 'react';

interface OverflowRowProps {
    children?: React.ReactNode;
    gap?: number | string;
    align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
    justify?: 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly';
    className?: string;
    renderOverflow?: (hiddenCount: number) => React.ReactNode;
}

const ALIGN_MAP: Record<NonNullable<OverflowRowProps['align']>, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
    baseline: 'baseline',
};

const JUSTIFY_MAP: Record<NonNullable<OverflowRowProps['justify']>, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    'space-between': 'space-between',
    'space-around': 'space-around',
    'space-evenly': 'space-evenly',
};

const HIDDEN_ATTR = 'data-overflow-row-hidden';
const STYLE_ID = 'malachite-overflow-row-style';
const STYLE_CONTENT = `[data-overflow-row] > * { flex-shrink: 0; min-width: 0; } [${HIDDEN_ATTR}] { display: none !important; }`;

// Sub-pixel tolerance for comparing summed child widths against the container.
const FIT_EPSILON_PX = 0.5;

const ensureHiddenStyle = (root: Document | ShadowRoot): void => {
    const existing =
        root instanceof Document
            ? root.getElementById(STYLE_ID)
            : root.querySelector(`#${STYLE_ID}`);
    if (existing) {
        return;
    }
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE_CONTENT;
    if (root instanceof Document) {
        root.head.appendChild(style);
    } else {
        root.appendChild(style);
    }
};

/**
 * Measurement is split into phases and batched across ALL OverflowRow instances:
 * first every queued row reveals its children (writes), then every row measures
 * (reads — a single forced layout for the whole batch), then every row applies
 * its hidden set (writes). Interleaving writes and reads per row — let alone per
 * child, as a hide-one-remeasure loop does — forces a full layout each time,
 * which froze Safari when many rows mounted in one commit (virtualized tables).
 */
interface MeasureInstance {
    isActive: () => boolean;
    reveal: () => void;
    measure: () => void;
    apply: () => void;
}

const pendingInstances = new Set<MeasureInstance>();
let flushQueued = false;

const flushMeasurements = (): void => {
    flushQueued = false;
    const batch = Array.from(pendingInstances);
    pendingInstances.clear();
    const active = batch.filter((instance) => instance.isActive());
    for (const instance of active) {
        instance.reveal();
    }
    for (const instance of active) {
        instance.measure();
    }
    for (const instance of active) {
        instance.apply();
    }
};

// Catch intrinsic child resizes (image load, style swap). Observe each child once —
// re-observing per pass is what fed the old reflow storm (ENG-1166). Takes the observer as
// an argument so the effect that allocates it is also the only thing that disconnects it.
const syncChildObservers = (observer: ResizeObserver | null, row: HTMLElement): void => {
    if (!observer) {
        return;
    }
    observer.disconnect();
    for (const child of Array.from(row.children) as HTMLElement[]) {
        observer.observe(child);
    }
};

// Microtask (not rAF) so a measure requested during React's commit still lands
// before paint — no flash of clipped children on mount.
const enqueueMeasurement = (instance: MeasureInstance): void => {
    pendingInstances.add(instance);
    if (!flushQueued) {
        flushQueued = true;
        queueMicrotask(flushMeasurements);
    }
};

const OverflowRow: React.FC<OverflowRowProps> = ({
    children,
    gap = 0,
    align = 'center',
    justify = 'start',
    className,
    renderOverflow,
}) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const [hiddenCount, setHiddenCount] = useState(0);

    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only on gap/align/justify; see dep-array comment
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) {
            return;
        }

        const rootNode = row.getRootNode();
        if (rootNode instanceof Document || rootNode instanceof ShadowRoot) {
            ensureHiddenStyle(rootNode);
        }

        const hasResizeObserver = typeof ResizeObserver !== 'undefined';
        const hasMutationObserver = typeof MutationObserver !== 'undefined';

        let cancelled = false;
        let lastWidth = -1;

        // Per-pass scratch shared between the phases of one flush.
        const scratch = {
            dataItems: [] as HTMLElement[],
            indicator: null as HTMLElement | null,
            itemWidths: [] as number[],
            indicatorWidth: 0,
            containerWidth: 0,
            gapPx: 0,
        };

        const instance: MeasureInstance = {
            isActive: () => !cancelled && rowRef.current === row && row.isConnected,
            reveal: () => {
                const items = Array.from(row.children) as HTMLElement[];
                const hasIndicator = !!renderOverflow;
                scratch.indicator = hasIndicator ? (items[items.length - 1] ?? null) : null;
                scratch.dataItems = hasIndicator ? items.slice(0, -1) : items;
                for (const item of scratch.dataItems) {
                    item.removeAttribute(HIDDEN_ATTR);
                }
                scratch.indicator?.removeAttribute(HIDDEN_ATTR);
            },
            measure: () => {
                scratch.containerWidth = row.getBoundingClientRect().width;
                scratch.itemWidths = scratch.dataItems.map(
                    (item) => item.getBoundingClientRect().width,
                );
                scratch.indicatorWidth = scratch.indicator?.getBoundingClientRect().width ?? 0;
                scratch.gapPx =
                    typeof gap === 'number'
                        ? gap
                        : Number.parseFloat(getComputedStyle(row).columnGap) || 0;
            },
            apply: () => {
                const { dataItems, indicator, itemWidths, indicatorWidth, containerWidth, gapPx } =
                    scratch;
                const totalWidth =
                    itemWidths.reduce((sum, width) => sum + width, 0) +
                    gapPx * Math.max(0, dataItems.length - 1);

                let visibleCount = dataItems.length;
                if (totalWidth > containerWidth + FIT_EPSILON_PX) {
                    // Overflowing: reserve the indicator (when present), then fit
                    // greedily. With an indicator, K visible items means K gaps
                    // (item1 gap … itemK gap indicator); without one it is K-1.
                    visibleCount = 0;
                    let used = indicator ? indicatorWidth : 0;
                    for (const width of itemWidths) {
                        const gapCost = indicator || visibleCount > 0 ? gapPx : 0;
                        const next = used + gapCost + width;
                        if (next > containerWidth + FIT_EPSILON_PX) {
                            break;
                        }
                        used = next;
                        visibleCount += 1;
                    }
                }

                const nextHidden = dataItems.length - visibleCount;
                for (let i = visibleCount; i < dataItems.length; i += 1) {
                    dataItems[i].setAttribute(HIDDEN_ATTR, '');
                }
                if (nextHidden === 0) {
                    indicator?.setAttribute(HIDDEN_ATTR, '');
                }
                setHiddenCount((prev) => (prev === nextHidden ? prev : nextHidden));
            },
        };

        const schedule = (): void => {
            if (!cancelled) {
                enqueueMeasurement(instance);
            }
        };

        schedule();

        const onRowResize = (entries: ResizeObserverEntry[]): void => {
            if (cancelled) {
                return;
            }
            // contentRect from the observer, not a clientWidth read — a layout
            // query here would force the layout this design avoids. lastWidth is
            // tracked exclusively in these units so the guard compares like with
            // like (getBoundingClientRect is border-box and rounds differently).
            const width = entries[0]?.contentRect.width;
            if (width !== undefined && Math.abs(width - lastWidth) < 1) {
                return;
            }
            if (width !== undefined) {
                lastWidth = width;
            }
            schedule();
        };
        const rowResize = hasResizeObserver ? new ResizeObserver(onRowResize) : null;
        rowResize?.observe(row);

        const childResize = hasResizeObserver ? new ResizeObserver(schedule) : null;
        syncChildObservers(childResize, row);

        // Re-sync child observers on add/remove only; HIDDEN_ATTR toggles are attribute
        // mutations and can't re-trigger this.
        const mutation = hasMutationObserver
            ? new MutationObserver(() => {
                  syncChildObservers(childResize, row);
                  schedule();
              })
            : null;
        mutation?.observe(row, { childList: true });

        // A row whose parent is content-sized never changes width once it has collapsed: the
        // hidden items shrink it, and the shrunken box is then the only thing observed, so it
        // can never learn that space came back. The viewport is the signal that survives that,
        // and a re-measure is idempotent (reveal() restores every item before measuring).
        const onViewportResize = (): void => schedule();
        window.addEventListener('resize', onViewportResize);

        if (typeof document !== 'undefined' && document.fonts?.ready) {
            document.fonts.ready.then(schedule).catch(() => undefined);
        }

        return () => {
            cancelled = true;
            pendingInstances.delete(instance);
            window.removeEventListener('resize', onViewportResize);
            rowResize?.disconnect();
            childResize?.disconnect();
            mutation?.disconnect();
        };
        // gap/align/justify change what fits at an unchanged width, so re-run on them.
    }, [gap, align, justify]);

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: ALIGN_MAP[align],
        justifyContent: JUSTIFY_MAP[justify],
        gap: typeof gap === 'number' ? `${gap}px` : gap,
        // Clip sideways only: `overflow: hidden` also shaved off the 1px shadow a Button
        // paints below itself. `clip`/`visible` is the one pairing CSS won't coerce.
        overflowX: 'clip',
        overflowY: 'visible',
        minWidth: 0,
        width: '100%',
    };

    return (
        <div ref={rowRef} className={className} style={containerStyle} data-overflow-row="">
            {children}
            {renderOverflow ? (
                <div
                    data-overflow-indicator=""
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                    {renderOverflow(hiddenCount)}
                </div>
            ) : null}
        </div>
    );
};

export default OverflowRow;
